import { createContext, useRef, useState, useEffect, useCallback, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';

// src/react.tsx

// src/feature-cache.ts
function createFeatureCache() {
  let entry;
  return {
    get(ttlMs) {
      if (!entry) return void 0;
      if (Date.now() - entry.fetchedAt > ttlMs) return void 0;
      return entry;
    },
    set(features) {
      entry = { features, fetchedAt: Date.now() };
    },
    clear() {
      entry = void 0;
    }
  };
}
var DEFAULT_FEATURE_TTL_MS = 6e4;
var OxboAuthContext = createContext(null);
function OxboAuthProvider({
  client,
  featureTtlMs = DEFAULT_FEATURE_TTL_MS,
  children
}) {
  const cacheRef = useRef(createFeatureCache());
  return /* @__PURE__ */ jsx(
    OxboAuthContext.Provider,
    {
      value: { client, cache: cacheRef.current, featureTtlMs },
      children
    }
  );
}
function useOxboAuthContext() {
  const ctx = useContext(OxboAuthContext);
  if (!ctx) {
    throw new Error(
      "useOxboAuthContext: must be used inside <OxboAuthProvider>."
    );
  }
  return ctx;
}
function useFeature(key) {
  const { client, cache, featureTtlMs } = useOxboAuthContext();
  const [features, setFeatures] = useState(
    () => cache.get(featureTtlMs)?.features ?? null
  );
  const [loading, setLoading] = useState(features === null);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const fetchFeatures = useCallback(
    async (invalidate = false) => {
      if (invalidate) cache.clear();
      const cached = cache.get(featureTtlMs);
      if (cached && !invalidate) {
        if (mountedRef.current) {
          setFeatures(cached.features);
          setLoading(false);
        }
        return;
      }
      if (mountedRef.current) setLoading(true);
      try {
        const res = await client.getFeatures();
        cache.set(res.features);
        if (mountedRef.current) {
          setFeatures(res.features);
          setError(null);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [client, cache, featureTtlMs]
  );
  useEffect(() => {
    void fetchFeatures();
  }, [fetchFeatures]);
  const refresh = useCallback(() => {
    void fetchFeatures(true);
  }, [fetchFeatures]);
  return {
    enabled: features?.[key] === true,
    loading,
    error,
    refresh
  };
}
function FeatureGate({
  feature,
  fallback = null,
  showFallbackWhileLoading = false,
  children
}) {
  const { enabled, loading } = useFeature(feature);
  if (loading) {
    return showFallbackWhileLoading ? fallback : null;
  }
  return enabled ? children : fallback;
}

export { FeatureGate, OxboAuthProvider, useFeature };
//# sourceMappingURL=react.js.map
//# sourceMappingURL=react.js.map